import { AlertTriangle, Trash2 } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from './';

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  isLoading?: boolean;
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  variant = 'danger',
  isLoading = false,
}) => {
  const { t } = useTranslation();

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const variantStyles = {
    danger: {
      icon: <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />,
      buttonClass: 'bg-red-600 hover:bg-red-700 text-white',
    },
    warning: {
      icon: <AlertTriangle className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />,
      buttonClass: 'bg-yellow-600 hover:bg-yellow-700 text-white',
    },
    info: {
      icon: <AlertTriangle className="h-6 w-6 text-blue-600 dark:text-blue-400" />,
      buttonClass: 'bg-blue-600 hover:bg-blue-700 text-white',
    },
  };

  const style = variantStyles[variant];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title || t('common.confirmAction')}
      size="md"
    >
      <div className="space-y-4">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">{style.icon}</div>
          <div className="flex-1">
            <p className="text-gray-600 dark:text-gray-400">{message}</p>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            {cancelText || t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            className={style.buttonClass}
            disabled={isLoading}
            loading={isLoading}
          >
            {confirmText || t('common.confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};


